import React, { useRef, useState } from 'react';
import { useTranslation } from '../i18n/useTranslation.js';
import AddressField from './AddressField.js';
import LocationPickerMap from './LocationPickerMap.js';
import { ZONE_TYPES, type Site } from '../api/types.js';
import { useSettings } from '../SettingsProvider.js';
import { searchAddressByCoords } from '../api/index.js';
import AsyncDebounce from '../utils/AsyncDebounce.js';

export interface NewZonePayload {
  name: string;
  type: string;
  address: string;
  lat: number;
  lng: number;
  alertRadius: number;
  boundaryType: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Creates a zone, or redefines `zone` when one is given. */
  onSubmit: (payload: NewZonePayload) => Promise<void>;
  /** Edit mode: the form starts from this zone. Remount (key) the modal when it changes. */
  zone?: Site;
}

function AddZoneModal({ open, onClose, onSubmit, zone }: Props) {
  const { t } = useTranslation();
  const { settings } = useSettings();

  const zoneOptions = ZONE_TYPES.map(({ value, icon }) => {
    let label = '';

    if (value === 'syn') {
      label = t('addZone.typeSynagogue');
    } else if (value === 'school') {
      label = t('addZone.typeSchool');
    } else if (value === 'community') {
      label = t('addZone.typeCommunity');
    } else if (value === 'memorial') {
      label = t('addZone.typeMemorial');
    }

    return { value, label, icon };
  });

  const BOUNDARY_TYPES = [
    { value: 'radius', label: t('addZone.boundaryRadius') },
    { value: 'polygon', label: t('addZone.boundaryPolygon') },
  ];

  const [name, setName] = useState(zone?.name ?? '');
  const [type, setType] = useState(zone?.type ?? 'syn');
  const [address, setAddress] = useState(zone?.addr ?? '');
  const [boundaryType, setBoundaryType] = useState('radius');
  const [alertRadius, setAlertRadius] = useState(250);
  const [lat, setLat] = useState(zone ? zone.lat.toFixed(6) : (settings?.mission.mapCenterLat.toFixed(6) ?? ''));
  const [lng, setLng] = useState(zone ? zone.lng.toFixed(6) : (settings?.mission.mapCenterLng.toFixed(6) ?? ''));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flyKey, setFlyKey] = useState(0);

  const updateAddressByCoords = useRef(
    AsyncDebounce(async (lat: number, lng: number) => {
      setFlyKey((k) => k + 1);
      try {
        const result = await searchAddressByCoords(lat, lng);
        if (result.address) {
          setAddress(result.address);
        }
      } catch {
        return;
      }
    }, 350)
  ).current;

  if (!open) return null;

  const reset = () => {
    setName('');
    setType(ZONE_TYPES[0].value);
    setAddress('');
    setBoundaryType(BOUNDARY_TYPES[0].value);
    setAlertRadius(250);
    setLat(settings?.mission.mapCenterLat.toFixed(6) ?? '');
    setLng(settings?.mission.mapCenterLng.toFixed(6) ?? '');
    setError(null);
    setFlyKey(0);
  };

  const handleMapPick = (pickedLat: number, pickedLng: number) => {
    const latValue = pickedLat.toFixed(6);
    const lngValue = pickedLng.toFixed(6);
    setLat(latValue);
    setLng(lngValue);
    updateAddressByCoords(+latValue, +lngValue);
  };

  const handleUpdateCoords = (pickedLat?: string, pickedLng?: string) => {
    if (pickedLat !== undefined) {
      setLat(pickedLat);
    }
    if (pickedLng !== undefined) {
      setLng(pickedLng);
    }
    updateAddressByCoords(pickedLat ? +pickedLat : +lat, pickedLng ? +pickedLng : +lng);
  };

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const canSubmit = name.trim() && address.trim() && lat.trim() && lng.trim();

  const handleSubmit = async () => {
    if (!canSubmit || saving) return;
    const latNum = Number(lat);
    const lngNum = Number(lng);
    if (Number.isNaN(latNum) || Number.isNaN(lngNum)) {
      setError(t('addZone.latLngError'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSubmit({
        name: name.trim(),
        type,
        address: address.trim(),
        lat: latNum,
        lng: lngNum,
        alertRadius,
        boundaryType,
      });
      reset();
      onClose();
    } catch (err: any) {
      setError(err?.message || t(zone ? 'addZone.updateFailed' : 'addZone.createFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mov" onClick={handleClose}>
      <div className="mod" onClick={(e) => e.stopPropagation()}>
        <div className="mhd">
          <span className="mt">{zone ? t('addZone.editTitle') : t('addZone.title')}</span>
          <div className="mx" onClick={handleClose}>
            ×
          </div>
        </div>
        <div className="mbd">
          <div className="msec">
            <div className="mst">{t('addZone.zoneDetails')}</div>
            <div className="fr">
              <div className="fg">
                <label className="fl">{t('addZone.locationName')}</label>
                <input
                  className="fi"
                  type="text"
                  placeholder={t('addZone.locationNamePlaceholder')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="fg">
                <label className="fl">{t('addZone.type')}</label>
                <select className="fsl" value={type} onChange={(e) => setType(e.target.value)}>
                  {zoneOptions.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.icon} {t.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="fr">
              <AddressField
                label={t('addZone.address')}
                placeholder={t('addZone.addressPlaceholder')}
                value={address}
                onChange={(loc) => {
                  setAddress(loc.address);
                  setLat(String(loc.lat));
                  setLng(String(loc.lng));
                  setFlyKey((k) => k + 1);
                }}
              />
            </div>
          </div>

          <div className="msec">
            <div className="mst">{t('addZone.zoneBoundary')}</div>
            <LocationPickerMap lat={lat} lng={lng} flyKey={flyKey} onPick={handleMapPick} />
            <div style={{ fontSize: 10, color: 'var(--t3)', marginTop: 6, marginBottom: 10, textAlign: 'center' }}>
              Double-click to place · Drag marker to reposition
            </div>
            <div className="fr">
              {/* <div className="fg">
                <label className="fl">{t('addZone.boundaryType')}</label>
                <select className="fsl" value={boundaryType} onChange={(e) => setBoundaryType(e.target.value)}>
                  {BOUNDARY_TYPES.map((b) => (
                    <option key={b.value} value={b.value}>
                      {b.label}
                    </option>
                  ))}
                </select>
              </div> */}
              {/* <div className="fg">
                <label className="fl">{t('addZone.alertRadius')}</label>
                <input
                  className="fi"
                  type="number"
                  min={50}
                  max={2000}
                  value={alertRadius}
                  onChange={(e) => setAlertRadius(Number(e.target.value))}
                />
              </div> */}
              <div className="fg">
                <label className="fl">{t('addZone.latitude')}</label>
                <input
                  className="fi"
                  type="text"
                  placeholder={t('addZone.latitudePlaceholder')}
                  value={lat}
                  onChange={(e) => handleUpdateCoords(e.target.value)}
                />
              </div>
              <div className="fg">
                <label className="fl">{t('addZone.longitude')}</label>
                <input
                  className="fi"
                  type="text"
                  placeholder={t('addZone.longitudePlaceholder')}
                  value={lng}
                  onChange={(e) => handleUpdateCoords(undefined, e.target.value)}
                />
              </div>
            </div>
          </div>

          {error && <div style={{ color: 'var(--red)', fontSize: 11, marginTop: 4 }}>{error}</div>}
        </div>
        <div className="mft">
          <button className="btn btn-g" onClick={handleClose} disabled={saving}>
            {t('addZone.cancel')}
          </button>
          <button className="btn btn-p" onClick={handleSubmit} disabled={!canSubmit || saving}>
            {zone
              ? saving
                ? t('addZone.saving')
                : t('addZone.save')
              : saving
                ? t('addZone.creating')
                : t('addZone.create')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddZoneModal;
